import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { TextInput } from "@/components/text-input";
import { Table } from "@/components/table";
import { Button } from "@/components/button";
import { useMemo, useState } from "@wordpress/element";
import { toast } from "react-toastify";
import { isURL } from "@/utils/validator";
import { api } from "@/config/api-fetch";
import { KbType, useKnowledgeBase, useKnowledgeBaseStats } from "@/hooks/use-knowledge-base";
import { useModal } from "@/hooks/use-modal";
import { IS_PRO } from "@/utils/constants";
import { useProModal } from "@/components/pro-alert-box";
import { useRoute } from "@/hooks/use-route";
import { getPaginatedList } from "@/utils/paginate";
import { OPTIONS } from "@/utils/constants";

export function WebsiteURL() {
    const { query, updateQuery } = useRoute();
    const page = query.page_no ? parseInt(query.page_no, 10) : 1;
    const limit = query.limit ? parseInt(query.limit, 10) : OPTIONS.PAGINATION_LIMIT;
    const search = query.search || "";
    const orderby = query.orderby || "";
    const order = (query.order as any) || "";

    const { refetch: refetchStats } = useKnowledgeBaseStats();
    const { data, refetch, isLoading } = useKnowledgeBase(KbType.url);
    const [submitting, setSubmitting] = useState(false);
    const [url, setURL] = useState<string>();
    const { confirm } = useModal();
    const { showProAlert } = useProModal();
    const [deletingIds, setDeletingIds] = useState<string[]>([]);

    const deleteFromKb = async (id: string) => {
        setDeletingIds((prev) => [...prev, id]);

        try {
            await api.delete(`/knowledge-base?knowledge_id=${id}`);
            await refetch();
            await refetchStats().catch(() => {});
            toast.success(__("URL has been deleted.", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setDeletingIds((prev) => prev.filter((prevId) => prevId !== id));
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirm(__("Do you want the AI to forget the information of this Webpage?", "antimanual"));

        if (confirmed) {
            await deleteFromKb(id);
        }
    };

    const handleBulkDelete = async (ids: string[]) => {
        if (ids.length === 0) {
            toast.error(__("Please select at least one URL.", "antimanual"));
            return;
        }

        const confirmed = await confirm(
            __("All of the selected URLs will be removed from the AI's memory. Do you still want to do that?", "antimanual")
        );

        if (!confirmed) return;

        for (const id of ids) {
            await deleteFromKb(id);
        }
    };

    const columns = [
        { id: "url", label: __("URL", "antimanual"), sortable: true },
        {
            id: "action",
            label: __("Action", "antimanual"),
        },
    ];

    const rows = useMemo(() => {
        if (!data) return [];
        let items = data;

        // Search
        if (search) {
             items = items.filter((item) => item.title.toLowerCase().includes(search.toLowerCase()));
        }

        // Sort
        if (orderby === 'url' && order) {
             items = [...items].sort((a, b) => {
                 return order === 'ASC' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
             });
        }

        const paginatedItems = getPaginatedList(items, page, limit);

        return paginatedItems.map((item) => ({
            id: item.id,
            url: (
                <a href={item.link} target="_blank">
                    {item.title}
                </a>
            ),
            action: (
                <Button small variant="danger" loading={deletingIds.includes(item.id)} onClick={() => handleDelete(item.id)}>
                    {__("Delete", "antimanual")}
                </Button>
            ),
        }));
    }, [data, deletingIds, search, orderby, order, page, limit]);

    const handleURLChange = (url?: string) => {
        setURL(url?.trim());
    };

    const handleUrlSubmit = async (url?: string) => {
        if (!url) {
            toast.error(__("URL is required.", "antimanual"));
            return;
        }

        if (!isURL(url)) {
            toast.error(__("The provided URL is invalid", "antimanual"));
            return;
        }

        setSubmitting(true);

        try {
            await api.post("/knowledge-base/url", { url });
            await refetch();
            await refetchStats().catch(() => {});
            toast.success(__("Website content has been added to the knowledge base.", "antimanual"));
            handleURLChange("");
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <div className={css("kb-option url-kb")}>
            <div className={css("flex items-end gap-2 mb-8")}>
                <div className={css("min-w-sm")}>
                    <TextInput
                        value={url}
                        onChange={(url) => {
                            const pattern = /^(https?:\/\/?)?/g;
                            handleURLChange(url?.replace(pattern, "https://"));
                        }}
                        label={__("Enter Website URL", "antimanual")}
                        placeholder={__("https://example.com", "antimanual")}
                    />
                </div>
                <Button
                    className={css("w-auto")}
                    onClick={() => {
                        if (!IS_PRO) {
                            showProAlert();
                            return;
                        }

                        handleUrlSubmit(url);
                    }}
                    loading={submitting}
                    disabled={submitting || !isURL(`${url}`)}
                >
                    {__("Submit", "antimanual")}
                </Button>
            </div>
            <Table
                rowsLoading={isLoading}
                columns={columns}
                rows={rows}
                actions={[{ value: "delete", label: __("Delete", "antimanual"), handler: handleBulkDelete }]}
                selectedAction="delete"
                pagination={{
                    total: (() => {
                         if (!data) return 0;
                         if (search) return data.filter((item) => item.title.toLowerCase().includes(search.toLowerCase())).length;
                         return data.length;
                    })(),
                    currentPage: page,
                    limit: limit,
                    onPageChange: (page) => updateQuery({ page_no: `${page}` }),
                    onLimitChange: (limit) => updateQuery({ limit: `${limit}`, page_no: "1" }),
                }}
                search={search}
                onSearch={(search) => updateQuery({ search, page_no: "1" })}
                sorting={{ orderby, order }}
                onSort={(orderby, order) => updateQuery({ orderby, order, page_no: "1" })}
            />
        </div>
    );
}
